import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, SimpleChanges, OnChanges, ChangeDetectorRef } from '@angular/core';
import { BaseComponent, WmsSpinnerComponent } from '@wms/components';
import { cloneDeep, orderBy } from 'lodash-es';
import { WmsTreeNodeComponent } from './wms-tree-node.component';
import { SearchComponent } from '../search/search.component';
import { isArrayEqual } from '@wms/core';
@Component({
  selector: 'wms-tree',
  standalone: true,
  templateUrl: './wms-tree.component.html',
  styleUrls: ['./wms-tree.component.less'],
  imports: [
    CommonModule,
    WmsTreeNodeComponent,
    SearchComponent,
    WmsSpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WmsTreeComponent extends BaseComponent implements OnInit, OnChanges {

  @Input() show: boolean = true;
  @Input() class!: string;
  @Input() data!: any;
  @Input() getChildren!: any;
  @Input() hasSearch!: boolean;
  @Input() restrictedSearch!: boolean;
  @Input() searchElementId!: string;
  @Input() searchElementName: string = 'text-tree-search-input';
  @Input() sortBy!: string;
  @Input() sortOrder: any = 'asc';
  @Input() iconType!: string;
  @Input() styles!: string;
  @Input() nodatamsg!: string;
  @Input() expandByDefault!: boolean;
  @Input() selectedNode!: any;
  @Input() externalSearchKey!: string;
  @Input() hideChildren?: boolean;
  @Input() skipChildNodeSearch?: boolean;
  @Input() preserveExpadedState?: boolean;
  @Input() expandOnClick?: boolean;
  @Input() enableNestedSearch! : boolean;
  @Input() enableDragDrop? : boolean = false;

  expandedNodes: any = [];

  @Output() nodeClick = new EventEmitter<any>();
  @Output() onExpand = new EventEmitter<any>();
  @Output() dataChanged = new EventEmitter<any[]>();

  filteredData: any;
  searchText: any = '';
  showSpinner:boolean = false;
  private draggedNode: any = null;
  // deleteIcon: any  = `<i class="wm-icon wms wms-delete"></i>`
  // loadingMsg: any  = `<div class="tree-loading-msg"><i class="icon"></i><span>Loading...</span></div>`
  // loadingUl: any   = `<wms-spinner caption="Loading..." size="md"></wms-spinner>`

  constructor(private cdr: ChangeDetectorRef) {
    super();

    this.registerDestroyListener.add(this.eventManager.expandTreeNodeEventObservable().subscribe((data: any) => {
      if (data) {
        this.expandParentNodes(data);  // Auto-expand parent nodes before finding element
      }
    }));
  }

  ngOnInit(): void {
    this.resetData();
  }

  ngOnChanges(changes: SimpleChanges) {
    const previousVal = changes['data']?.previousValue;
    if (changes['data'] && ((previousVal === undefined || previousVal?.length === 0) && this.data?.length) ||
      (this.data?.length && previousVal?.length &&
        ((this.data.length === previousVal?.length && !isArrayEqual(this.data, previousVal)) || (this.data.length !== previousVal.length)))) {
      this.resetData();
    }
    /* If search key comes from parent component */
    if (changes['externalSearchKey']?.previousValue !== changes['externalSearchKey']?.currentValue) {
      this.onSearch({
        searchKey: changes['externalSearchKey'].currentValue
      });
    }
    if(!this.data){
      this.resetData();
    }
  }

  searchFilter(treeData: Array<any>, searchTerm: string) {
    let localFilteredList: any = [];
    if (!Array.isArray(treeData)) return localFilteredList;
    treeData.forEach((node) => {
      if ((this.hideChildren || this.skipChildNodeSearch) && node.searchable) {
        if (this.hasText(node?.id, searchTerm)) {
          localFilteredList.push(node);
        }
      } else if (this.hasText(node?.id, searchTerm)) {
        const filterData = (node.children && Array.isArray(node?.children)) ? this.searchFilter(node.children, searchTerm) : [];
        if (filterData.length) node.children = filterData;
        localFilteredList.push(node);
      } else {
        let filteredChildren = this.searchFilter(node?.children, searchTerm);
        if (filteredChildren.length)
          localFilteredList.push(Object.assign({}, node, { children: filteredChildren }));
      }
    })
    return localFilteredList;
  }

  isPromise(data: Promise<any>) {
    if (data instanceof Promise) {
        return true;
    }
    return false;
  }

  filterDataBySearchKey(): any {
    if (this.searchText.length) {
      let localList = this.searchFilter(cloneDeep(this.data), this.searchText);
      this.filteredData = localList;
    }
    else {
      this.resetData();
    }
  }

  onSearch(event: any) {
    this.searchText = event.searchKey;
    if(typeof this.getChildren === 'function' && this.searchText && this.enableNestedSearch){
      this.showSpinner = true
      this.processgetChildren()
    }else{
      this.filterDataBySearchKey();
    }
  }

  async processgetChildren(){
    await Promise.all(this.data.map((node: any) => this.processNodeAndChildren(node)));
    this.filterDataBySearchKey()
    this.showSpinner = false;
    this.cdr.detectChanges();
  }

  async processNodeAndChildren(node: any) {
    if (!node.hasChildren || node.children?.length) return; // Exit if node has no children
  
    const childrenNodes = await this.getChildrenIfNeeded(node);
  
    if (childrenNodes) {
      node.children = childrenNodes;
      await Promise.all(node.children.map((child: any) => this.processNodeAndChildren(child)));
    }
  }


  async getChildrenIfNeeded(node: any): Promise<any[] | null> {
    const childrenNodes = this.getChildren(node);
    return this.isPromise(childrenNodes) ? await childrenNodes : null;
  }

  resetData() {
    this.filteredData = cloneDeep(orderBy(this.data, this.sortBy, this.sortOrder));
  }

  hasText(s1: string, s2: string): boolean {
    return s1?.toLowerCase().includes(s2?.toLowerCase());
  }

  canExpandNode(nodeId: any): boolean {
    return nodeId && this.expandedNodes.length ? (this.expandByDefault || this.expandedNodes.includes(nodeId)) : this.expandByDefault;
  }

  onNodeExpand(node: any) {
    if (this.preserveExpadedState) {
      let { id, isExpanded } = node;
      if (isExpanded) this.expandedNodes.push(id);
      else { this.expandedNodes = this.expandedNodes.filter((n: any) => n !== id) }
    }
  }

  onNodeDragStart(event: any): void {
    this.draggedNode = event.dragData;
  }

  onNodeDragEnd(event: any): void {
    this.draggedNode = null;
  }

  onNodeDropped(event: any): void {
    if (!this.draggedNode) return;

    const { dragData, targetNode, targetParent, position } = event;
    this.moveNode(dragData, targetNode, targetParent, position);
    // Emit data change
    this.dataChanged.emit(event);
  }

  moveNode(sourceNode: any, targetNode: any, targetParent: string, position: string): void {
    this.removeNodeFromTreeInPlace(this.filteredData, sourceNode.nodeId);
    this.addNodeToTreeInPlace(this.filteredData, sourceNode.dataItem, targetNode.id, position);
  }

  private removeNodeFromTreeInPlace(tree: any[], nodeId: string): boolean {
    const index = tree.findIndex(node => node.id === nodeId);
    if (index > -1) {
      tree.splice(index, 1);
      return true;
    }
    for (const node of tree) {
      if (node.children && this.removeNodeFromTreeInPlace(node.children, nodeId)) {
        return true;
      }
    }
    return false;
  }

  private addNodeToTreeInPlace(tree: any[], nodeToAdd: any, targetId: string, position: string): boolean {
    for (let i = 0; i < tree.length; i++) {
      const node = tree[i];
      if (node.id === targetId) {
        if (position === 'inside') {
          node.children = node.children || [];
          node.children.push(nodeToAdd);
        } else {
          const insertIndex = position === 'prev' ? i : i + 1;
          tree.splice(insertIndex, 0, nodeToAdd);
        }
        return true;
      }
      if (node.children && this.addNodeToTreeInPlace(node.children, nodeToAdd, targetId, position)) {
        return true;
      }
    }
    return false;
  }

  trackById(index: number, node: any) {
    return node.id;
  }

   /** * Find all parent widget IDs for a given widget */
  private findParentPath(widgetId: string, tree: any[], path: string[] = []): string[] | null {
    for (const node of tree) {
      const nodeWidgetId = node.props?.widgetId;
      
      if (nodeWidgetId === widgetId) {
        return path;
      }
      if (node.children && node.children.length > 0) {
        const result = this.findParentPath(widgetId, node.children, [...path, node.id]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  //Auto-expand all parent nodes to make the target node visible
  private expandParentNodes(widgetId: string): void {
    const parentPath = this.findParentPath(widgetId, this.data);
    if (parentPath) {
      this.expandedNodes = parentPath;
      setTimeout(() => this.cdr.detectChanges(), 0);
    }
  }

}
